/**
 * submission.ts — the Submission / Versand / Erinnerung contract (P2 Placement Slice 8,
 * Ausschreibungs-Screen Tab 3b, Prozessschritte 6 + 7; Detailkonzept Ausschreibung V3.0
 * Ch 6.5 / 8.2 / 8.3 / 9 / 10 / 11).
 *
 * The SINGLE SOURCE OF TRUTH for:
 *   - the Submission status vocabulary + the 10.4 status machine (+ E1 nicht_offeriert, Ch 8.1 inaktiv),
 *   - the Submissions-Nummer format `SUB-YYYY-NNNNNN-KKKK` (Ch 8.2) + the IGB2B-Familien-Kürzel map,
 *   - the Frist arithmetic (Arbeitstage, expiry flag) + the send-lock window,
 *   - the wire shapes (list / erstellen / versenden / erinnern / status / frist / unterlagen),
 *   - the header-chip overlay (`chipsMitSubmission`) + the counters (`submissionZaehler`).
 *
 * Pure module — no I/O, no formatters, no Date-TZ parse (UTC string arithmetic only, the Slice-6 lesson).
 *
 * [Source: docs/product/p2-slices/slice-8-submission-versand-design.md §3]
 * [Source: packages/shared/src/placement-versicherer.ts] (triad + chip + Sperrgrund idioms)
 * [Source: packages/shared/src/police-risiko.ts] (POLICE_RISIKO_SOURCE triad idiom)
 */

import { igb2bFamilyOf } from './kunde-risikoprofil';
import type { PlacementStatus, PlacementClosureStatus } from './placement';
import {
  isVersichererAuswahlErlaubt,
  type PlacementCarrierChip,
  type VersichererAuswahlSperrgrund,
} from './placement-versicherer';

// ── Vocabularies (triad idiom) ───────────────────────────────────────────────

/** The lifecycle status of one submission (Ch 10.4 + E1 nicht_offeriert + Ch 8.1 inaktiv). */
export const SUBMISSION_STATUS = {
  NEU: 'neu',
  VERSANDT: 'versandt',
  ERINNERT: 'erinnert',
  EINGEGANGEN: 'eingegangen',
  NICHT_OFFERIERT: 'nicht_offeriert',
  INAKTIV: 'inaktiv',
} as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUS)[keyof typeof SUBMISSION_STATUS];
export const SUBMISSION_STATUS_VALUES: readonly SubmissionStatus[] = [
  SUBMISSION_STATUS.NEU,
  SUBMISSION_STATUS.VERSANDT,
  SUBMISSION_STATUS.ERINNERT,
  SUBMISSION_STATUS.EINGEGANGEN,
  SUBMISSION_STATUS.NICHT_OFFERIERT,
  SUBMISSION_STATUS.INAKTIV,
];
export const SUBMISSION_STATUS_LABELS: Record<SubmissionStatus, string> = {
  [SUBMISSION_STATUS.NEU]: 'neu',
  [SUBMISSION_STATUS.VERSANDT]: 'versandt',
  [SUBMISSION_STATUS.ERINNERT]: 'erinnert',
  [SUBMISSION_STATUS.EINGEGANGEN]: 'eingegangen',
  [SUBMISSION_STATUS.NICHT_OFFERIERT]: 'nicht offeriert',
  [SUBMISSION_STATUS.INAKTIV]: 'inaktiv',
};

/** Which kind of outbound mail a submission_email row records. */
export const SUBMISSION_EMAIL_ART = {
  VERSAND: 'versand',
  ERINNERUNG: 'erinnerung',
} as const;
export type SubmissionEmailArt = (typeof SUBMISSION_EMAIL_ART)[keyof typeof SUBMISSION_EMAIL_ART];
export const SUBMISSION_EMAIL_ART_VALUES: readonly SubmissionEmailArt[] = [
  SUBMISSION_EMAIL_ART.VERSAND,
  SUBMISSION_EMAIL_ART.ERINNERUNG,
];
export const SUBMISSION_EMAIL_ART_LABELS: Record<SubmissionEmailArt, string> = {
  [SUBMISSION_EMAIL_ART.VERSAND]: 'Versand',
  [SUBMISSION_EMAIL_ART.ERINNERUNG]: 'Erinnerung',
};

/** The four Ausschreibungsunterlagen-artefakte attached at Versand (Ch 8.3). */
export const SUBMISSION_ATTACHMENT_ART = {
  RISIKOPROFIL: 'risikoprofil',
  SOLL_DECKUNG: 'soll_deckung',
  SCHADENVERLAUF: 'schadenverlauf',
  SCHADENVERLAUF_JSON: 'schadenverlauf_json',
} as const;
export type SubmissionAttachmentArt =
  (typeof SUBMISSION_ATTACHMENT_ART)[keyof typeof SUBMISSION_ATTACHMENT_ART];
export const SUBMISSION_ATTACHMENT_ART_VALUES: readonly SubmissionAttachmentArt[] = [
  SUBMISSION_ATTACHMENT_ART.RISIKOPROFIL,
  SUBMISSION_ATTACHMENT_ART.SOLL_DECKUNG,
  SUBMISSION_ATTACHMENT_ART.SCHADENVERLAUF,
  SUBMISSION_ATTACHMENT_ART.SCHADENVERLAUF_JSON,
];
export const SUBMISSION_ATTACHMENT_ART_LABELS: Record<SubmissionAttachmentArt, string> = {
  [SUBMISSION_ATTACHMENT_ART.RISIKOPROFIL]: 'Risikoprofil',
  [SUBMISSION_ATTACHMENT_ART.SOLL_DECKUNG]: 'Soll-Deckungsvorgaben',
  [SUBMISSION_ATTACHMENT_ART.SCHADENVERLAUF]: 'Schadenverlauf',
  [SUBMISSION_ATTACHMENT_ART.SCHADENVERLAUF_JSON]: 'Schadenverlauf (strukturiert)',
};

// ── Status machine (D2) ───────────────────────────────────────────────────────

/**
 * The legal submission transitions (Ch 10.4 + E1 + Ch 8.1). Frist expiry is a FLAG
 * (`frist_eskaliert_at`), not a status, so it is absent here.
 *   - `erinnert → erinnert` is the "wiederholbar" self-loop.
 *   - `eingegangen` is reachable ONLY via the exported `markEingegangenTx` (Slice 10).
 *   - `nicht_offeriert → eingegangen`: a late offer trumps a decline (Q64, Slice 10's call).
 */
export const SUBMISSION_STATUS_TRANSITIONS: Record<SubmissionStatus, readonly SubmissionStatus[]> = {
  neu: ['versandt', 'inaktiv'],
  versandt: ['erinnert', 'eingegangen', 'nicht_offeriert', 'inaktiv'],
  erinnert: ['erinnert', 'eingegangen', 'nicht_offeriert', 'inaktiv'],
  eingegangen: [],
  nicht_offeriert: ['eingegangen'],
  inaktiv: [],
};

/** Is `to` a legal next status from `from`? The single guard for every submission transition. */
export function canTransitionSubmission(from: SubmissionStatus, to: SubmissionStatus): boolean {
  return SUBMISSION_STATUS_TRANSITIONS[from].includes(to);
}

/** The legal next statuses from `from` (a copy). */
export function submissionErlaubteTransitionen(from: SubmissionStatus): SubmissionStatus[] {
  return [...SUBMISSION_STATUS_TRANSITIONS[from]];
}

/** Terminal (no outgoing transition). */
export const SUBMISSION_TERMINAL_STATUSES: readonly SubmissionStatus[] = ['inaktiv'];
/** From which status a submission can be sent («Versenden»). */
export const SUBMISSION_SENDABLE_STATUSES: readonly SubmissionStatus[] = ['neu'];
/** From which status a submission can be reminded («Erinnern»). */
export const SUBMISSION_REMINDABLE_STATUSES: readonly SubmissionStatus[] = ['versandt', 'erinnert'];
/** From which status «nicht offeriert» may be marked. */
export const SUBMISSION_DECLINABLE_STATUSES: readonly SubmissionStatus[] = ['versandt', 'erinnert'];
/** From which status «inaktiv markieren» (Ch 8.1) may be marked. */
export const SUBMISSION_DEACTIVATABLE_STATUSES: readonly SubmissionStatus[] = [
  'neu',
  'versandt',
  'erinnert',
];
/** In which status the Frist may be edited. */
export const SUBMISSION_FRIST_EDITABLE_STATUSES: readonly SubmissionStatus[] = [
  'neu',
  'versandt',
  'erinnert',
];

// ── Erstellen / Aktion gate (reuses Slice 7) ────────────────────────────────────

/** The placement statuses in which Offertanfragen may be created/sent (= Slice 7's Auswahl gate). */
export const SUBMISSION_ERSTELLEN_ERLAUBTE_STATUS: readonly PlacementStatus[] = [
  'vorgaben_erfasst',
  'ausgeschrieben',
];

/** Null when the Submission-Aktionen are allowed, else the Sperrgrund (reuses the Slice-7 gate). */
export function isSubmissionAktionErlaubt(
  status: PlacementStatus,
  closure: PlacementClosureStatus,
): VersichererAuswahlSperrgrund | null {
  return isVersichererAuswahlErlaubt(status, closure);
}

// ── Number (Ch 8.2) ─────────────────────────────────────────────────────────────

/** `SUB-2026-000482-HAFT` — year + 6-digit running number (per tenant/year) + IGB2B-family Kürzel. */
export const SUBMISSION_NUMBER_REGEX = /^SUB-(\d{4})-(\d{6})-([A-Z]{4})$/;
/** Loose scan used by Slice 9 to find a Nummer inside a reply subject. */
export const SUBMISSION_NUMBER_IN_TEXT_REGEX = /SUB-\d{4}-\d{6}-[A-Z]{4}/;

/** The 9 IGB2B families (`igb2b_kategorie` heads) → 4-letter Kürzel (Ch 8.2). */
export const IGB2B_FAMILIE_KUERZEL: Record<string, string> = {
  '10000': 'FAHR',
  '20000': 'HAFT',
  '30000': 'UNFA',
  '40000': 'KRAN',
  '50000': 'TRAN',
  '60000': 'SACH',
  '70000': 'TECH',
  '80000': 'UEBR',
  '90000': 'LEBE',
};

/** The Kürzel of the IGB2B family a branchencode belongs to (unknown → 'UEBR'). */
export function submissionKuerzelFor(branchencode: string): string {
  return IGB2B_FAMILIE_KUERZEL[igb2bFamilyOf(branchencode)] ?? 'UEBR';
}

/** `SUB-${jahr}-${seq padded to 6}-${kuerzel}`. */
export function formatSubmissionNumber(jahr: number, seq: number, kuerzel: string): string {
  return `SUB-${jahr}-${String(seq).padStart(6, '0')}-${kuerzel}`;
}

/** Parse a Submissions-Nummer into its parts, or null when it does not match. */
export function parseSubmissionNumber(
  text: string,
): { jahr: number; seq: number; kuerzel: string } | null {
  const m = SUBMISSION_NUMBER_REGEX.exec(text);
  if (!m) return null;
  return { jahr: parseInt(m[1], 10), seq: parseInt(m[2], 10), kuerzel: m[3] };
}

// ── Frist (D5) ──────────────────────────────────────────────────────────────────

/** Default Antwortfrist in Arbeitstagen (Ch 8.3 «z. B. 10 Arbeitstage»). */
export const SUBMISSION_FRIST_DEFAULT_ARBEITSTAGE = 10;
/** Upper bound for a manually set Frist (service 400 `frist_invalid`). */
export const SUBMISSION_FRIST_MAX_TAGE = 120;
/** Minutes after which a `versand_gestartet_at` lock is stale and a resend is allowed. */
export const SUBMISSION_VERSAND_LOCK_MINUTES = 10;

/**
 * Add `n` Arbeitstage (Mon-Fri, no holiday calendar — Q59) to an ISO date (`YYYY-MM-DD`).
 * Pure UTC-string arithmetic, never a TZ `Date` parse (the Slice-6 lesson). `n=0` = identity.
 */
export function addArbeitstage(isoDate: string, n: number): string {
  const [y, m, d] = isoDate.split('-').map((s) => parseInt(s, 10));
  let ms = Date.UTC(y, m - 1, d);
  let added = 0;
  while (added < n) {
    ms += 86_400_000;
    const day = new Date(ms).getUTCDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  const dt = new Date(ms);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Is the Frist expired (and the submission still awaiting an answer)? `frist < today && status ∈ {versandt, erinnert}`. */
export function isSubmissionFristAbgelaufen(
  fristDate: string | null,
  status: SubmissionStatus,
  today: string,
): boolean {
  if (fristDate === null) return false;
  if (status !== 'versandt' && status !== 'erinnert') return false;
  return fristDate < today;
}

// ── Texts ─────────────────────────────────────────────────────────────────────

export const SUBMISSION_KEINE_AUSWAHL_HINWEIS =
  'Zuerst Versicherer auswählen (Schritt 5), dann Offertanfragen erstellen.';
export const SUBMISSION_KEINE_EMAIL_HINWEIS =
  'Keine Ausschreibungs-E-Mail hinterlegt, der Versand an diesen Versicherer ist gesperrt, bis die Adresse beim Versicherer ergänzt ist.';
export const SUBMISSION_MAILBOX_HINWEIS =
  'Für den Versand muss Ihr eigenes Postfach verbunden sein (Einstellungen → Postfach).';
export const SUBMISSION_FRIST_ABGELAUFEN_LABEL = 'Frist abgelaufen';
export const SUBMISSION_VORSCHAU_HINWEIS = 'Vorschau - noch nicht versandt';

// ── Events (Ch 11 + Slice-8 additions) ──────────────────────────────────────────

export const SUBMISSION_EVENTS = {
  CREATED: 'submission.created',
  SENT: 'submission.sent',
  REMINDER_SENT: 'submission.reminder_sent',
  DECLINED: 'submission.declined',
  DEACTIVATED: 'submission.deactivated',
  FRIST_EXPIRED: 'submission.frist_expired',
  BOUNCED: 'submission.bounced',
} as const;
export type SubmissionEvent = (typeof SUBMISSION_EVENTS)[keyof typeof SUBMISSION_EVENTS];

// ── Wire types ──────────────────────────────────────────────────────────────────

export interface SubmissionEmailEintrag {
  id: string;
  art: SubmissionEmailArt;
  communication_id: string;
  message_id: string | null;
  empfaenger: string;
  betreff: string;
  attachment_count: number;
  sent_at: string;
  sent_by: string | null;
  sent_by_name: string | null;
  bounce_at: string | null;
  bounce_grund: string | null;
}

export interface SubmissionAttachmentEintrag {
  id: string;
  art: SubmissionAttachmentArt;
  filename: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
  created_at: string;
}

export interface SubmissionEintrag {
  id: string;
  placement_id: string;
  carrier_selection_id: string;
  carrier_uid: string;
  name: string | null;
  logo_url: string | null;
  submission_number: string;
  status: SubmissionStatus;
  status_label: string;
  status_changed_at: string;
  /** The address the mail went to (frozen on the submission at Versand). */
  carrier_email_to: string | null;
  /** The firm's effective submission address now (own ?? global); may differ from `carrier_email_to` (PR#43-B1). */
  email_aktuell: string | null;
  submission_faehig: boolean;
  frist_date: string | null;
  frist_abgelaufen: boolean;
  frist_eskaliert_at: string | null;
  frist_task_id: string | null;
  sent_at: string | null;
  sent_by: string | null;
  sent_by_name: string | null;
  erinnert_count: number;
  letzte_erinnerung_at: string | null;
  received_at: string | null;
  nicht_offeriert_at: string | null;
  nicht_offeriert_grund: string | null;
  inaktiv_at: string | null;
  inaktiv_grund: string | null;
  /** A FRESH send-in-flight lock (younger than SUBMISSION_VERSAND_LOCK_MINUTES) → «Versand läuft». */
  versand_laeuft: boolean;
  /** A stale lock (crash between send + record) OR a recorded `record_failed` → «Versand unterbrochen». */
  versand_unterbrochen: boolean;
  /** PR #43 wave 2 (#5): a `record_failed` marker - every send on this row is blocked until resolved. */
  versand_gesperrt: boolean;
  /** PR #43 wave 2 (#5): the stored comm id (+ frist/email where `neu`) make `nachtragen` possible. */
  nachtragen_moeglich: boolean;
  letzter_versandfehler: string | null;
  letzter_versandfehler_at: string | null;
  erlaubte_transitionen: SubmissionStatus[];
  emails: SubmissionEmailEintrag[];
  attachments: SubmissionAttachmentEintrag[];
  created_at: string;
  updated_at: string;
}

/** Why the Submission-Aktionen are blocked (null = allowed; same vocab as Slice 7). */
export type SubmissionSperrgrund = VersichererAuswahlSperrgrund;

export interface SubmissionListResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  branchencode: string;
  kuerzel: string;
  aktion_erlaubt: boolean;
  sperrgrund: SubmissionSperrgrund | null;
  /** ausgewaehlt selection rows without a submission (= how many «erstellen» would create). */
  erstellbar: number;
  neu: number;
  versandt: number;
  erinnert: number;
  eingegangen: number;
  nicht_offeriert: number;
  inaktiv: number;
  frist_abgelaufen: number;
  /** The caller has a non-revoked mailbox connection (pre-flight hint, not a guarantee). */
  mailbox_verbunden: boolean;
  /** addArbeitstage(today, 10) — what «Versenden» uses unless overridden. */
  frist_default_date: string;
  /** created_at ASC. */
  items: SubmissionEintrag[];
  /** Slice-7 chips overlaid with submission_status/number/frist. */
  chips: PlacementCarrierChip[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- reserved body, symmetric with the other action bodies
export interface SubmissionErstellenBody {}

export interface SubmissionErstellenResponse extends SubmissionListResponse {
  /** submission ids created this call. */
  erstellt: string[];
  /** carrier uids skipped (already carried a submission_id). */
  uebersprungen: string[];
}

export interface SubmissionVersendenBody {
  submission_ids?: string[];
  /** ISO date (YYYY-MM-DD). */
  frist_date?: string;
}

export type SubmissionVersandFehlerCode =
  | 'carrier_email_missing'
  | 'mailbox_not_connected'
  | 'mailbox_reauth_required'
  | 'mail_send_blocked'
  | 'mailbox_send_failed'
  | 'attachment_too_large'
  | 'versand_laeuft'
  | 'versand_unterbrochen'
  | 'status_not_neu'
  | 'unbekannt';

export interface SubmissionVersandErgebnis {
  submission_id: string;
  submission_number: string;
  carrier_uid: string;
  code: SubmissionVersandFehlerCode | null;
  message: string | null;
}

export interface SubmissionVersendenResponse extends SubmissionListResponse {
  versendet: SubmissionVersandErgebnis[];
  fehlgeschlagen: SubmissionVersandErgebnis[];
  uebersprungen: SubmissionVersandErgebnis[];
  abgebrochen: boolean;
  placement_transitioned: boolean;
  hinweise: string[];
}

export interface SubmissionErinnernBody {
  /** Optional extra paragraph (≤ 2000). */
  nachricht?: string;
}

export interface SubmissionNichtOfferiertBody {
  /** ≤ 2000. */
  grund?: string;
}

export interface SubmissionInaktivBody {
  /** 3..2000. */
  grund: string;
}

export interface SubmissionFristBody {
  frist_date: string;
}

export interface SubmissionUnterlagenVorschauInfo {
  art: SubmissionAttachmentArt;
  filename: string;
  mime_type: string;
  vorschau: boolean;
  size_bytes: number | null;
  /** #8: set on a stored file the reading user may not download (content-right); the row stays, the bytes 403. */
  hidden_reason: 'permission' | null;
}

export interface SubmissionUnterlagenResponse {
  submission_id: string;
  submission_number: string;
  status: SubmissionStatus;
  unterlagen: SubmissionUnterlagenVorschauInfo[];
  hinweise: string[];
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Overlay the Slice-7 header chips with the live submission state (left-join by carrier_uid):
 * sets `submission_status` / `submission_number` / `frist_date` / `frist_abgelaufen` and swaps the
 * label to the submission-status label (+ « · Frist abgelaufen»). Chips without a submission are
 * returned untouched (the Slice-7 label stays).
 */
export function chipsMitSubmission(
  chips: readonly PlacementCarrierChip[],
  items: readonly SubmissionEintrag[],
): PlacementCarrierChip[] {
  const byUid = new Map<string, SubmissionEintrag>();
  for (const it of items) byUid.set(it.carrier_uid, it);
  return chips.map((chip) => {
    const sub = byUid.get(chip.carrier_uid);
    if (!sub) return { ...chip };
    const label =
      SUBMISSION_STATUS_LABELS[sub.status] +
      (sub.frist_abgelaufen ? ` · ${SUBMISSION_FRIST_ABGELAUFEN_LABEL}` : '');
    return {
      ...chip,
      status_label: label,
      submission_status: sub.status,
      submission_number: sub.submission_number,
      frist_date: sub.frist_date,
      frist_abgelaufen: sub.frist_abgelaufen,
    };
  });
}

/** The 7 list counters derived from the items. */
export function submissionZaehler(items: readonly SubmissionEintrag[]): {
  neu: number;
  versandt: number;
  erinnert: number;
  eingegangen: number;
  nicht_offeriert: number;
  inaktiv: number;
  frist_abgelaufen: number;
} {
  const z = {
    neu: 0,
    versandt: 0,
    erinnert: 0,
    eingegangen: 0,
    nicht_offeriert: 0,
    inaktiv: 0,
    frist_abgelaufen: 0,
  };
  for (const it of items) {
    z[it.status] += 1;
    if (it.frist_abgelaufen) z.frist_abgelaufen += 1;
  }
  return z;
}
